<template>
    <CRow>
        <CCol col="12" xl="12">
            <CCard v-if="!loading">
                <CCardHeader class="d-flex justify-content-between align-items-center">
                    <strong>Тригерная рассылка #{{ campaign.id }}</strong>
                    <div>
                        <CButton
                            color="info"
                            size="sm"
                            class="mr-2"
                            :to="`/trigger-campaigns/${campaign.id}/edit`">
                            Редактировать
                        </CButton>
                        <CButton
                            color="secondary"
                            size="sm"
                            to="/trigger-campaigns">
                            Назад к списку
                        </CButton>
                    </div>
                </CCardHeader>
                <CCardBody>
                    <CRow>
                        <CCol md="6">
                            <table class="table table-striped">
                                <tbody>
                                    <tr>
                                        <td><strong>ID:</strong></td>
                                        <td>{{ campaign.id }}</td>
                                    </tr>
                                    <tr>
                                        <td><strong>Название:</strong></td>
                                        <td>{{ campaign.name || 'Без названия' }}</td>
                                    </tr>
                                    <tr>
                                        <td><strong>Сегмент:</strong></td>
                                        <td>{{ campaign.segment_name || 'Все пользователи' }}</td>
                                    </tr>
                                    <tr>
                                        <td><strong>Тип триггера:</strong></td>
                                        <td>{{ getTriggerTypeLabel(campaign.trigger_type) }}</td>
                                    </tr>
                                    <tr v-if="campaign.trigger_type !== 'cart_abandoned' && campaign.trigger_type !== 'checkout_abandoned'">
                                        <td><strong>Количество дней:</strong></td>
                                        <td>{{ campaign.trigger_days }}</td>
                                    </tr>
                                    <tr v-else>
                                        <td><strong>Количество часов:</strong></td>
                                        <td>{{ campaign.trigger_hours }}</td>
                                    </tr>
                                    <tr v-if="campaign.send_schedule">
                                        <td><strong>Расписание отправки:</strong></td>
                                        <td>
                                            <code>{{ campaign.send_schedule }}</code>
                                            <div class="text-muted small mt-1">
                                                {{ getScheduleDescription(campaign.send_schedule) }}
                                            </div>
                                        </td>
                                    </tr>
                                    <tr v-else>
                                        <td><strong>Время отправки:</strong></td>
                                        <td>{{ campaign.send_time }}</td>
                                    </tr>
                                    <tr>
                                        <td><strong>Статус:</strong></td>
                                        <td>
                                            <CBadge :color="getBadgeColor(campaign.enabled)">
                                                {{ campaign.enabled ? "Активна" : "Неактивна" }}
                                            </CBadge>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td><strong>Дата создания:</strong></td>
                                        <td>{{ formatDate(campaign.created_at) }}</td>
                                    </tr>
                                    <tr>
                                        <td><strong>Дата обновления:</strong></td>
                                        <td>{{ formatDate(campaign.updated_at) }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </CCol>
                        <CCol md="6">
                            <div class="h5">Содержание рассылки</div>
                            <div class="card">
                                <div class="card-body">
                                    <h6 class="card-title">{{ campaign.push_title || 'Без заголовка' }}</h6>
                                    <p class="card-text">{{ campaign.push_text || 'Без текста' }}</p>
                                    <small class="text-muted">
                                        <strong>Ссылка при клике:</strong> 
                                        {{ campaign.click_url || 'Не указана' }}
                                    </small>
                                </div>
                            </div>
                        </CCol>
                    </CRow>

                    <!-- Статистика отправок (если есть) -->
                    <CRow class="mt-4" v-if="statistics.length > 0">
                        <CCol col="12">
                            <h5>Статистика отправок</h5>
                            <CDataTable
                                hover
                                :items="statistics"
                                :fields="statisticsFields">
                                <template #sent_at="data">
                                    <td>{{ formatDate(data.item.sent_at) }}</td>
                                </template>
                                <template #status="data">
                                    <td>
                                        <CBadge :color="getStatusBadgeColor(data.item.status)">
                                            {{ getStatusLabel(data.item.status) }}
                                        </CBadge>
                                    </td>
                                </template>
                            </CDataTable>
                        </CCol>
                    </CRow>
                </CCardBody>
            </CCard>
            <div v-else class="text-center">
                <CSpinner color="primary"/>
            </div>
        </CCol>
    </CRow>
</template>

<script>
    import Vue from 'vue';
    const { formatDate } = require('../../utils/views');

    export default {
        name: 'TriggerCampaign',

        data() {
            return {
                loading: true,
                campaign: {},
                statistics: [],
                statisticsFields: [
                    { key: 'customer_id', label: 'ID пользователя' },
                    { key: 'sent_at', label: 'Дата отправки' },
                    { key: 'status', label: 'Статус' },
                    { key: 'error_message', label: 'Ошибка' }
                ],
                triggerTypes: {
                    'registration': 'После регистрации',
                    'last_order': 'После последнего заказа',
                    'cart_abandoned': 'Брошенная корзина',
                    'checkout_abandoned': 'Брошенная оплата'
                }
            };
        },

        async mounted() {
            await this.fetch();
            this.loading = false;
        },

        methods: {
            formatDate,
            
            getBadgeColor(status) {
                return status ? 'success' : 'secondary';
            },

            getStatusBadgeColor(status) {
                const colors = {
                    'sent': 'success',
                    'failed': 'danger',
                    'pending': 'warning'
                };
                return colors[status] || 'secondary';
            },

            getStatusLabel(status) {
                const labels = {
                    'sent': 'Отправлено',
                    'failed': 'Ошибка',
                    'pending': 'В ожидании'
                };
                return labels[status] || status;
            },

            getTriggerTypeLabel(type) {
                return this.triggerTypes[type] || type;
            },

            getScheduleDescription(schedule) {
                const scheduleDescriptions = {
                    '0 9-18 * * *': 'Каждый час с 9:00 до 18:00',
                    '0 10-20 * * *': 'Каждый час с 10:00 до 20:00',
                    '0 */2 * * *': 'Каждые 2 часа',
                    '0 9,12,15,18 * * *': 'В 9:00, 12:00, 15:00 и 18:00',
                    '30 10-16 * * *': 'Каждый час в 30 минут с 10:30 до 16:30',
                    '0 9-18 * * 1-5': 'Каждый час с 9:00 до 18:00 (пн-пт)'
                };
                return scheduleDescriptions[schedule] || 'Пользовательское расписание';
            },

            async fetch() {
                try {
                    const campaignId = this.$route.params.id;
                    const response = await Vue.axios.get(`/trigger-campaigns/${campaignId}`);
                    this.campaign = response.data;

                    // Загружаем статистику отправок
                    try {
                        const statsResponse = await Vue.axios.get(`/trigger-campaigns/${campaignId}/statistics`);
                        this.statistics = statsResponse.data;
                    } catch (err) {
                        // Статистика не обязательна
                        console.warn('Статистика недоступна:', err);
                    }
                } catch (err) {
                    this.$root.$emit('showError', 'Ошибка загрузки тригерной рассылки.');
                    console.error(err);
                }
            }
        }
    };
</script>
